import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import HTMLFlipBook from 'react-pageflip';
import amyImg from '../assets/images/amy-front.png';

// Dummy Books Data
const CIMI_CIMO_PAGES = [
    '/assets/books/cover-front.jpg',
    ...Array.from({ length: 16 }, (_, index) => `/assets/books/scan-${index + 1}.jpg`),
    '/assets/books/cover-back.jpg',
];
const DEFAULT_BOOK_PAGE_SIZE = { width: 1000, height: 1400 };
const PAGE_PLACEHOLDER_IMAGE = '/assets/books/page-transparent.png';
const INITIAL_PREFETCH_COUNT = 4;
const WELCOME_AMY_ENTER_DELAY_MS = 1500;
const WELCOME_SCENE_TRANSITION_SEC = 1.5;
const WELCOME_CARD_REVEAL_DELAY_SEC = 0.5;
const WELCOME_AMY_IDLE_DURATION_SEC = 0.5;
const WELCOME_AMY_ENTER_DURATION_SEC = 1.5;
const ENTERING_ZOOM_DURATION_SEC = 2;
const CORRIDOR_BG_PATH = '/assets/images/castle-corridor.png';
const LIBRARY_BLOOM_DURATION_SEC = 1.1;
const LIBRARY_BLOOM_HOLD_MS = 120;
const BOOK_TO_READING_ANIMATION_MS = 420;
const MOBILE_LANDSCAPE_LOGO_WIDTH_PX = 96;

const BOOKS = [
    {
        id: 1,
        title: "Cimi & Cimo",
        coverImage: CIMI_CIMO_PAGES.find((path) => path.includes('cover-front')) || PAGE_PLACEHOLDER_IMAGE,
        position: { top: '46%', left: '28%', rotate: 4 },
        pageImages: CIMI_CIMO_PAGES,
        pageSize: { width: 1200, height: 848 },
    },
];

const ShelfHTML = () => (
    <div className="w-100 h-100 position-absolute top-0 start-0" style={{ pointerEvents: 'none' }}>
        {/* Sides */}
        <div style={{ position: 'absolute', left: '10%', top: '8%', bottom: 0, width: '2.5%', backgroundColor: '#5D4037' }} />
        <div style={{ position: 'absolute', right: '10%', top: '8%', bottom: 0, width: '2.5%', backgroundColor: '#5D4037' }} />

        {/* Shelves */}
        {[40, 75].map((top) => (
            <div key={top} style={{
                position: 'absolute',
                left: '12.5%',
                width: '75%',
                top: `${top}%`,
                height: '3.5%',
                background: 'linear-gradient(to bottom, #8D6E63, #5D4037)',
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
            }} />
        ))}
    </div>
);

// FlipBook Components
const BookSVG = ({ title, coverImage, onClick, position, isOpening }) => (
    <motion.button
        type="button"
        whileHover={isOpening ? undefined : { scale: 1.06, y: -8 }}
        whileTap={isOpening ? undefined : { scale: 0.97 }}
        animate={isOpening ? { y: -48, scale: 1.42, opacity: 0 } : { y: 0, scale: 1, opacity: 1 }}
        transition={{ duration: BOOK_TO_READING_ANIMATION_MS / 1000, ease: [0.22, 1, 0.36, 1] }}
        onClick={onClick}
        style={{
            cursor: 'pointer',
            border: 'none',
            background: 'transparent',
            padding: 0,
            position: 'absolute',
            zIndex: 10,
            width: '96px',
            ...position
        }}
    >
        <div
            style={{
                width: '96px',
                height: '132px',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                border: '2px solid rgba(255,255,255,0.78)',
                backgroundColor: '#fff'
            }}
        >
            <img
                src={coverImage}
                alt={`${title} cover`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
        </div>
        <div
            style={{
                marginTop: '8px',
                textAlign: 'center',
                fontSize: '13px',
                lineHeight: 1.2,
                fontWeight: 700,
                color: '#ffffff',
                textShadow: '0 1px 4px rgba(0,0,0,0.85)'
            }}
        >
            {title}
        </div>
    </motion.button>
);

const BookCover = React.forwardRef(({ image, title }, ref) => (
    <div className="page page-cover" ref={ref} data-density="hard">
        <div className="page-content">
            <img src={image} alt={title} style={{ width: '100%' }} />
        </div>
    </div>


));

const BookPage = React.forwardRef(({ image, number, transparent = false }, ref) => (
    <div className="page" ref={ref}>
        <div className="page-content">
            <img src={image} alt={`Page ${number}`} style={{ width: '100%' }} />
        </div>
    </div>

));

const StoryTime = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [stage, setStage] = useState('welcome'); // welcome, entering, library, reading
    const [amyAnimation, setAmyAnimation] = useState('idle'); // idle, entering
    const [selectedBook, setSelectedBook] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [menuBarHeight, setMenuBarHeight] = useState(76);
    const [, bumpImageCacheVersion] = useState(0);
    const [isPreparingBook, setIsPreparingBook] = useState(false);
    const [enteringZoomActive, setEnteringZoomActive] = useState(false);
    const [libraryBloomActive, setLibraryBloomActive] = useState(false);
    const [libraryBloomVisible, setLibraryBloomVisible] = useState(false);
    const [openingBookId, setOpeningBookId] = useState(null);
    const imageCacheRef = useRef(new Set());
    const openRequestRef = useRef(0);
    const enteringToLibraryTimeoutRef = useRef(null);
    const libraryBloomStartTimeoutRef = useRef(null);
    const libraryBloomTimeoutRef = useRef(null);

    useEffect(() => {
        const syncViewport = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            setViewportSize({ width, height });
            setIsMobile(width < 768);

            const stickyNavbar = document.querySelector('.sticky-navbar.is-visible') || document.querySelector('.sticky-navbar');
            const nextMenuBarHeight = stickyNavbar ? Math.ceil(stickyNavbar.getBoundingClientRect().height) : 76;
            setMenuBarHeight(nextMenuBarHeight);
        };

        syncViewport();
        window.addEventListener('resize', syncViewport);
        window.addEventListener('scroll', syncViewport, { passive: true });
        return () => {
            window.removeEventListener('resize', syncViewport);
            window.removeEventListener('scroll', syncViewport);
        };
    }, []);

    const triggerLibraryBloom = () => {
        if (libraryBloomStartTimeoutRef.current) {
            clearTimeout(libraryBloomStartTimeoutRef.current);
        }
        if (libraryBloomTimeoutRef.current) {
            clearTimeout(libraryBloomTimeoutRef.current);
        }
        setLibraryBloomActive(true);
        setLibraryBloomVisible(true);
        libraryBloomStartTimeoutRef.current = setTimeout(() => {
            setLibraryBloomVisible(false);
        }, LIBRARY_BLOOM_HOLD_MS);

        const timeoutDelayMs = LIBRARY_BLOOM_HOLD_MS + (LIBRARY_BLOOM_DURATION_SEC * 1000) + 60;
        libraryBloomTimeoutRef.current = setTimeout(() => {
            setLibraryBloomActive(false);
        }, timeoutDelayMs);
    };

    useEffect(() => {
        if (stage !== 'library') {
            if (libraryBloomStartTimeoutRef.current) {
                clearTimeout(libraryBloomStartTimeoutRef.current);
            }
            if (libraryBloomTimeoutRef.current) {
                clearTimeout(libraryBloomTimeoutRef.current);
            }
            setLibraryBloomActive(false);
            setLibraryBloomVisible(false);
        }
    }, [stage]);

    useEffect(() => () => {
        if (enteringToLibraryTimeoutRef.current) {
            clearTimeout(enteringToLibraryTimeoutRef.current);
        }
        if (libraryBloomStartTimeoutRef.current) {
            clearTimeout(libraryBloomStartTimeoutRef.current);
        }
        if (libraryBloomTimeoutRef.current) {
            clearTimeout(libraryBloomTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        const isReading = stage === 'reading' && Boolean(selectedBook);
        if (!isReading) {
            return undefined;
        }

        const html = document.documentElement;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = html.style.overflow;

        document.body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalBodyOverflow;
            html.style.overflow = originalHtmlOverflow;
        };
    }, [selectedBook, stage]);

    const handleEnter = () => {
        setAmyAnimation('entering');
        setTimeout(() => {
            const startEntering = () => {
                setEnteringZoomActive(false);
                setStage('entering');

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setEnteringZoomActive(true);
                    });
                });

                if (enteringToLibraryTimeoutRef.current) {
                    clearTimeout(enteringToLibraryTimeoutRef.current);
                }
                enteringToLibraryTimeoutRef.current = setTimeout(() => {
                    triggerLibraryBloom();
                    setStage('library');
                }, ENTERING_ZOOM_DURATION_SEC * 1000);
            };

            if (imageCacheRef.current.has(CORRIDOR_BG_PATH)) {
                startEntering();
                return;
            }

            const corridorImage = new Image();
            corridorImage.decoding = 'async';
            corridorImage.onload = () => {
                imageCacheRef.current.add(CORRIDOR_BG_PATH);
                bumpImageCacheVersion((value) => value + 1);
                startEntering();
            };
            corridorImage.onerror = () => {
                startEntering();
            };
            corridorImage.src = CORRIDOR_BG_PATH;
        }, WELCOME_AMY_ENTER_DELAY_MS);
    };

    const preloadImage = (src) => new Promise((resolve) => {
        if (!src || imageCacheRef.current.has(src)) {
            resolve();
            return;
        }

        const img = new Image();
        img.decoding = 'async';
        img.onload = () => {
            imageCacheRef.current.add(src);
            bumpImageCacheVersion((value) => value + 1);
            resolve();
        };
        img.onerror = () => resolve();
        img.src = src;
    });

    const handleOpenBook = async (book) => {
        const openRequestId = openRequestRef.current + 1;
        openRequestRef.current = openRequestId;

        setIsPreparingBook(true);
        setOpeningBookId(book.id);
        setSelectedBook(book);

        const openingPages = (book.pageImages || []).slice(0, INITIAL_PREFETCH_COUNT);
        await Promise.all([
            Promise.all(openingPages.map(preloadImage)),
            new Promise((resolve) => setTimeout(resolve, BOOK_TO_READING_ANIMATION_MS)),
        ]);

        if (openRequestRef.current !== openRequestId) {
            setIsPreparingBook(false);
            setOpeningBookId(null);
            return;
        }

        setStage('reading');
        setIsPreparingBook(false);
        setOpeningBookId(null);
    };

    const handleCloseBook = () => {
        if (enteringToLibraryTimeoutRef.current) {
            clearTimeout(enteringToLibraryTimeoutRef.current);
        }
        openRequestRef.current += 1;
        setIsPreparingBook(false);
        setOpeningBookId(null);
        setSelectedBook(null);
        setStage('library');
    };

    const handleRestartJourney = () => {
        if (enteringToLibraryTimeoutRef.current) {
            clearTimeout(enteringToLibraryTimeoutRef.current);
        }
        openRequestRef.current += 1;
        setIsPreparingBook(false);
        setOpeningBookId(null);
        setSelectedBook(null);
        setAmyAnimation('idle');
        setStage('welcome');
    };

    useEffect(() => {
        const pages = selectedBook?.pageImages || [];
        if (stage !== 'reading' || pages.length <= INITIAL_PREFETCH_COUNT) {
            return undefined;
        }

        let cancelled = false;
        const remainingPages = pages.slice(INITIAL_PREFETCH_COUNT);

        const warmRemainingPages = async () => {
            for (const pageImage of remainingPages) {
                if (cancelled) {
                    break;
                }
                await preloadImage(pageImage);
            }
        };

        warmRemainingPages();

        return () => {
            cancelled = true;
        };
    }, [selectedBook, stage]);

    const activeBookPageSize = selectedBook?.pageSize || DEFAULT_BOOK_PAGE_SIZE;
    const isLibraryEnabled = new URLSearchParams(location.search).get('library') === '1';
    const { pageWidth, pageHeight } = useMemo(() => {
        const sourceWidth = activeBookPageSize.width || DEFAULT_BOOK_PAGE_SIZE.width;
        const sourceHeight = activeBookPageSize.height || DEFAULT_BOOK_PAGE_SIZE.height;
        const aspectRatio = sourceWidth / sourceHeight;

        const horizontalPadding = isMobile ? 16 : 48;
        const verticalPadding = isMobile ? 16 : 24;
        const availableWidth = Math.max(280, viewportSize.width - horizontalPadding * 2);
        const availableHeight = Math.max(320, viewportSize.height - menuBarHeight - verticalPadding * 2);

        let computedWidth = isMobile ? availableWidth : availableWidth / 2;
        let computedHeight = computedWidth / aspectRatio;

        if (computedHeight > availableHeight) {
            computedHeight = availableHeight;
            computedWidth = computedHeight * aspectRatio;
        }

        return {
            pageWidth: Math.max(220, Math.floor(computedWidth)),
            pageHeight: Math.max(280, Math.floor(computedHeight)),
        };
    }, [activeBookPageSize.height, activeBookPageSize.width, isMobile, menuBarHeight, viewportSize.height, viewportSize.width]);
    const isMobileLandscape = isMobile && viewportSize.width > viewportSize.height;
    const readingTopPadding = isMobileLandscape ? 92 : (isMobile ? 58 : 72);

    if (!isLibraryEnabled) {
        return null;
    }

    return (
        <section className="story-time-section position-relative overflow-hidden" style={{ minHeight: '100vh', height: '100vh', backgroundColor: '#e0f7fa' }}>

            {/* STAGE: WELCOME */}
            {stage === 'welcome' && (
                <motion.div
                    key="welcome"
                    initial={{ opacity: 0, scale: 1 }}
                    animate={amyAnimation === 'entering' ? { scale: 1.5 } : { opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.5 }}
                    transition={{ duration: WELCOME_SCENE_TRANSITION_SEC }}
                    className="w-100 h-100 position-absolute top-0 start-0 d-flex flex-column align-items-center justify-content-center"
                    style={{
                        backgroundImage: `url(/assets/images/castle.png)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'bottom center',
                        transformOrigin: '50% 85%' // Anchor zoom to the castle door area
                    }}
                >
                    <Container className="text-center position-relative" style={{ zIndex: 2 }}>
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: WELCOME_CARD_REVEAL_DELAY_SEC }}
                            className="bg-white p-4 rounded-3 shadow-lg d-inline-block mb-4"
                            style={{ maxWidth: '400px' }}
                        >
                            <h3>{t('storybook-journey-title')}</h3>
                            <p>{t('storybook-journey-line1')}</p>
                            <Button variant="primary" size="lg" onClick={handleEnter}>
                                {t('storybook-journey-cta-door')}
                            </Button>
                        </motion.div>
                    </Container>

                    <motion.img
                        src={amyImg}
                        alt="Amy"
                        initial={{ x: -100, opacity: 0, scale: 1 }}
                        animate={amyAnimation === 'entering' ? { left: '48%', bottom: '10%', scale: 0.3, opacity: 0 } : { left: '15%', opacity: 1, scale: 1 }}
                        transition={{ delay: 0, duration: amyAnimation === 'entering' ? WELCOME_AMY_ENTER_DURATION_SEC : WELCOME_AMY_IDLE_DURATION_SEC }}
                        style={{ height: '32vh', position: 'absolute', bottom: '5%', left: '15%', zIndex: 1, transformOrigin: 'bottom center' }}
                    />
                </motion.div>
            )}

            {/* STAGE: ENTERING (Transition) */}
            {stage === 'entering' && (
                <div
                    key="entering"
                    className="w-100 h-100 position-absolute top-0 start-0"
                >
                    <div
                        style={{
                            transformOrigin: 'center 80%', // Anchor zoom to the door at the end of the corridor
                            backgroundImage: `url(${CORRIDOR_BG_PATH})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center 60%',
                            width: '100%',
                            height: '100%',
                            transform: enteringZoomActive ? 'scale(3)' : 'scale(1)',
                            transition: `transform ${ENTERING_ZOOM_DURATION_SEC}s ease-in`,
                            willChange: 'transform',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                        className="w-100 h-100"
                    >
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                background: 'radial-gradient(circle at 50% 63%, rgba(255,247,209,0.98) 0%, rgba(255,233,163,0.76) 17%, rgba(255,215,125,0.36) 34%, rgba(255,255,255,0) 62%)',
                                opacity: enteringZoomActive ? 1 : 0.42,
                                transform: enteringZoomActive ? 'scale(1.12)' : 'scale(0.94)',
                                transition: `opacity ${ENTERING_ZOOM_DURATION_SEC}s ease-in, transform ${ENTERING_ZOOM_DURATION_SEC}s ease-in`,
                                mixBlendMode: 'screen',
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,244,214,0.22) 44%, rgba(255,222,150,0.38) 66%, rgba(255,255,255,0) 95%)',
                                opacity: enteringZoomActive ? 0.92 : 0.32,
                                transition: `opacity ${ENTERING_ZOOM_DURATION_SEC}s ease-in`,
                            }}
                        />
                    </div>
                </div>
            )}

            {/* STAGE: LIBRARY */}
            {(stage === 'library' || stage === 'reading') && (
                <motion.div
                    key="library"
                    initial={false}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-100 h-100 position-absolute top-0 start-0"
                    style={{
                        backgroundImage: `url(/assets/images/grand-library.jpg)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                >
                    {libraryBloomActive && stage === 'library' && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 8,
                                pointerEvents: 'none',
                                backgroundColor: '#ffffff',
                                opacity: libraryBloomVisible ? 1 : 0,
                                transition: `opacity ${LIBRARY_BLOOM_DURATION_SEC}s cubic-bezier(0.22, 1, 0.36, 1)`,
                            }}
                        />
                    )}
                    <div className="position-absolute top-0 end-0 m-3" style={{ zIndex: 4, display: 'none' }}>
                        <Button variant="light" onClick={handleRestartJourney} style={{}}>
                            {t('storybook-journey-restart')}
                        </Button>
                    </div>
                    <Container className="h-100 position-relative d-flex flex-column justify-content-center" style={{ zIndex: 2 }}>
                        <Row>
                            <Col xs={12} className="text-center mb-4">
                                <h2 className="mb-4" style={{ color: '#fff', textShadow: '2px 2px 4px #000' }}>{t('storybook-journey-line3')}</h2>
                                <p
                                    style={{
                                        color: '#fff',
                                        textShadow: '0 2px 8px rgba(0,0,0,0.85)',
                                        fontWeight: 700,
                                        fontSize: isMobile ? '1rem' : '1.15rem',
                                        display: 'inline-block',
                                        padding: isMobile ? '8px 14px' : '10px 18px',
                                        borderRadius: '999px',
                                        backgroundColor: 'rgba(0,0,0,0.15)',
                                        border: '1px solid rgba(255,255,255,0.45)'
                                    }}
                                >
                                    {t('storybook-journey-line4')}
                                </p>
                            </Col>
                        </Row>
                        <div className="position-relative mx-auto" style={{ width: '100%', maxWidth: '600px', aspectRatio: '800/600' }}>
                            <ShelfHTML />
                            {BOOKS.map((book) => (
                                <BookSVG
                                    key={book.id}
                                    title={book.title}
                                    coverImage={book.coverImage}
                                    position={book.position}
                                    isOpening={openingBookId === book.id}
                                    onClick={() => {
                                        if (!isPreparingBook) {
                                            handleOpenBook(book);
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </Container>
                    <motion.img src={amyImg} alt="Amy" initial={{ x: 100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }} style={{ height: '30vh', position: 'absolute', bottom: '5%', right: '10%', zIndex: 3, filter: 'drop-shadow(2px 4px 6px black)' }} />
                    {isPreparingBook && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: 700,
                                textShadow: '0 2px 10px rgba(0,0,0,0.8)',
                                backgroundColor: 'rgba(12, 18, 30, 0.35)',
                            }}
                        >
                            Loading book...
                        </div>
                    )}
                </motion.div>
            )}

            {/* STAGE: READING */}
            {stage === 'reading' && selectedBook && typeof document !== 'undefined' && createPortal(
                <motion.div
                    key="reading"
                    initial={{ opacity: 0, backgroundColor: 'rgba(16, 20, 31, 0)' }}
                    animate={{ opacity: 1, backgroundColor: 'rgba(16, 20, 31, 0.86)' }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="d-flex justify-content-center align-items-center"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2000,
                        padding: isMobile ? '16px' : '24px',
                        backdropFilter: 'blur(3px)',
                    }}
                >
                    <img
                        src="/assets/logo.svg"
                        alt="Childreams"
                        style={{
                            position: 'absolute',
                            top: isMobileLandscape ? '8px' : (isMobile ? '12px' : '20px'),
                            left: isMobileLandscape ? '10px' : (isMobile ? '12px' : '20px'),
                            width: isMobileLandscape ? `${MOBILE_LANDSCAPE_LOGO_WIDTH_PX}px` : (isMobile ? '132px' : '168px'),
                            height: 'auto',
                            maxHeight: '13vh',
                            zIndex: 2001,
                            pointerEvents: 'none',
                        }}
                    />

                    <button
                        type="button"
                        className="btn btn-light rounded-pill px-3 py-2"
                        style={{
                            position: 'absolute',
                            top: isMobileLandscape ? '8px' : (isMobile ? '32px' : '20px'),
                            right: isMobileLandscape ? '10px' : (isMobile ? '15px' : '20px'),
                            zIndex: 2001,
                            fontWeight: 600,
                            fontSize: isMobileLandscape ? '0.95rem' : undefined,
                            padding: isMobileLandscape ? '0.5rem 0.85rem' : undefined,
                        }}
                        onClick={handleCloseBook}
                        aria-label={t('storybook-reader-close')}
                    >
                        × {t('storybook-reader-close')}
                    </button>

                    <motion.div
                        className="d-flex justify-content-center align-items-center w-100 h-100"
                        initial={{ opacity: 0, scale: 0.96, y: 18 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                        style={{ paddingTop: `${readingTopPadding}px` }}
                    >
                        <div
                            style={{
                                borderRadius: isMobile ? '12px' : '18px',
                                overflow: 'visible',
                                boxShadow: '0 18px 44px rgba(0, 0, 0, 0.35)',
                            }}
                        >
                            <HTMLFlipBook
                                width={pageWidth}
                                height={pageHeight}
                                size="fixed"
                                drawShadow={true}
                                showCover={true}
                                usePortrait={isMobile}
                                flippingTime={450}
                                mobileScrollSupport={true}
                                style={{
                                    borderRadius: isMobile ? '12px' : '18px',
                                }}
                            >
                                {(selectedBook?.pageImages || []).map((imagePath, index) => {
                                    const normalizedPath = imagePath.toLowerCase();
                                    const isCover = normalizedPath.includes('cover-front') || normalizedPath.includes('cover-back');
                                    const isTransparentPage = normalizedPath.includes('page-transparent');
                                    const isImageReady = imageCacheRef.current.has(imagePath);
                                    const imageSource = isImageReady ? imagePath : PAGE_PLACEHOLDER_IMAGE;
                                    return isCover ? (
                                        <BookCover key={`${imagePath}-${index}`} image={imageSource} title={selectedBook.title} />
                                    ) : (
                                        <BookPage key={`${imagePath}-${index}`} image={imageSource} number={index + 1} transparent={isTransparentPage || !isImageReady} />
                                    );
                                })}
                            </HTMLFlipBook>
                        </div>
                    </motion.div>
                </motion.div>,
                document.body
            )}

        </section>
    );
};

export default StoryTime;
